import { Users } from "lucide-react-native";
import {
  ComponentRef,
  FC,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Platform, Text as RNText, ScrollView } from "react-native";

import {
  HStack,
  Pressable,
  Skeleton,
  Text,
  Textarea,
  TextareaInput,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { Panel } from "@/types/resources";

/* Same metrics trick as `QuestionTextInput`: the real input renders its text
   transparent (caret stays visible) over a mirror that paints `@mentions` in
   color, so both layers must share font size, line height and padding. */
const FONT_SIZE = 16;
const LINE_HEIGHT = 24;
const MENTION_COLOR = "rgb(124, 58, 237)"; // primary-600
const MENU_PAGE_SIZE = 10;
const PLAIN_COLOR = "rgb(38, 38, 39)"; // typography-900
const QUERY_DEBOUNCE_MS = 400;

interface Mention {
  /** Text typed after `@`, up to the caret. */
  query: string;
  /** Index of the `@` character in the text. */
  start: number;
}

interface Segment {
  key: string;
  mention: boolean;
  text: string;
}

interface PanelMentionInputProps {
  /** Toolbar rendered inside the input box, under the text — attachments and
      their previews, composer-style. */
  footer?: ReactNode;
  isDisabled?: boolean;
  onChangeText: (text: string) => void;
  /** Fired when a panel is picked from the `@` dropdown. The `@query` token
      is already replaced by `@PanelName` in the text passed to
      `onChangeText`. */
  onSelectPanel: (panel: Panel) => void;
  placeholder: string;
  /** Panels already mentioned — their `@Name` tokens get highlighted and they
      are hidden from the dropdown. */
  selectedPanels: Panel[];
  value: string;
}

interface MentionMenuProps {
  excludeIds: string[];
  onSelect: (panel: Panel) => void;
  query: string;
}

/**
 * The `@` token being typed at the caret: the last `@` before the caret that
 * starts a word, with no newline between it and the caret, and that is not
 * already a completed mention of a selected panel.
 */
const findMention = (
  text: string,
  caret: number,
  selectedPanels: Panel[]
): Mention | null => {
  const before = text.slice(0, caret);
  const start = before.lastIndexOf("@");
  if (start === -1) return null;
  if (start > 0 && !/\s/.test(before[start - 1])) return null;
  const query = before.slice(start + 1);
  if (query.includes("\n") || query.includes("  ")) return null;
  // This `@` already holds a selected panel (name followed by a boundary):
  // whatever follows is prose, not a search.
  if (
    selectedPanels.some(
      (panel) =>
        query.startsWith(panel.name) &&
        (query.length === panel.name.length ||
          /\s/.test(query[panel.name.length]))
    )
  )
    return null;
  return { query, start };
};

/**
 * The panels whose `@Name` appears in `text` as a whole mention. Longest name
 * first, so "@Gen Z snackers" is only "Gen Z snackers", never also "Gen Z".
 */
export const findMentionedPanels = (text: string, panels: Panel[]): Panel[] => {
  const mentioned = new Set(
    toSegments(text, panels)
      .filter((segment) => segment.mention)
      .map((segment) => segment.text.slice(1))
  );
  return panels.filter((panel) => mentioned.has(panel.name));
};

/** The `@` at `at` starts a word and is followed by one of `names` as a whole
    mention (end of text or whitespace after it). */
const mentionAt = (
  text: string,
  at: number,
  names: string[]
): string | undefined => {
  if (text[at] !== "@" || (at > 0 && !/\s/.test(text[at - 1]))) return;
  return names.find((name) => {
    const end = at + 1 + name.length;
    return (
      text.startsWith(name, at + 1) &&
      (end === text.length || /\s/.test(text[end]))
    );
  });
};

/** Split `text` into plain / mention segments for the highlight mirror. */
const toSegments = (text: string, selectedPanels: Panel[]): Segment[] => {
  // Longest first so "Gen Z snackers" wins over "Gen Z".
  const names = selectedPanels
    .map((panel) => panel.name)
    .sort((a, b) => b.length - a.length);
  const segments: Segment[] = [];
  const push = (mention: boolean, chunk: string) => {
    if (chunk)
      segments.push({ key: `${segments.length}`, mention, text: chunk });
  };

  let plainStart = 0;
  let i = 0;
  while (i < text.length) {
    const name = mentionAt(text, i, names);
    if (name) {
      push(false, text.slice(plainStart, i));
      push(true, `@${name}`);
      i += 1 + name.length;
      plainStart = i;
      continue;
    }
    i++;
  }
  push(false, text.slice(plainStart));
  return segments;
};

/**
 * Dropdown body. Mounts on a bare `@` with the first panels, then refetches
 * only once the typed query has settled for `QUERY_DEBOUNCE_MS`.
 */
const MentionMenu: FC<MentionMenuProps> = ({ excludeIds, onSelect, query }) => {
  const { i18n } = useLocalization();
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    if (query === debouncedQuery) return;
    const handle = setTimeout(
      () => setDebouncedQuery(query),
      QUERY_DEBOUNCE_MS
    );
    return () => clearTimeout(handle);
  }, [debouncedQuery, query]);

  const { error, loading, panels } = API.Panels.useGetPanels({
    items_per_page: MENU_PAGE_SIZE,
    name: debouncedQuery,
  });

  const candidates = useMemo(
    () => panels.filter((panel) => !excludeIds.includes(panel.id)),
    [excludeIds, panels]
  );

  /* Hidden while the user is still typing; it reappears once the query has
     settled and the matching search runs. */
  if (query !== debouncedQuery) return null;

  /* Multi-word queries are mostly prose written after a mention (or a longer
     panel name being typed): stay hidden, including while loading, unless
     something actually matches. */
  if (candidates.length === 0 && query.includes(" ")) return null;

  return (
    <VStack className="w-72 max-h-56 overflow-hidden rounded-xl border border-outline-200 bg-background-0 shadow-md p-1">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        {loading && candidates.length === 0 ? (
          <VStack className="gap-1 p-1">
            {[1, 2, 3].map((i) => (
              <Skeleton className="h-9 w-full rounded-lg" key={i} />
            ))}
          </VStack>
        ) : error ? (
          <Text className="text-sm text-error-600 p-3">{error}</Text>
        ) : candidates.length === 0 ? (
          <Text className="text-sm text-typography-500 p-3">
            {i18n.t("studies.design.noPanelsFound")}
          </Text>
        ) : (
          candidates.map((panel) => (
            <Pressable key={panel.id} onPress={() => onSelect(panel)}>
              <HStack className="items-center gap-2 py-2 px-3 rounded-lg hover:bg-background-50">
                <Users color="rgb(var(--color-typography-500))" size={14} />
                <VStack className="flex-1">
                  <Text
                    className="text-sm font-medium text-typography-900"
                    numberOfLines={1}
                  >
                    {panel.name}
                  </Text>
                  {panel.description && (
                    <Text
                      className="text-xs text-typography-500"
                      numberOfLines={1}
                    >
                      {panel.description}
                    </Text>
                  )}
                </VStack>
              </HStack>
            </Pressable>
          ))
        )}
      </ScrollView>
    </VStack>
  );
};

/**
 * Textarea with an `@` panel picker: typing `@` + a character opens a
 * dropdown of the user's saved panels filtered by what follows; picking one
 * inserts `@PanelName` (highlighted) and reports the panel to the parent.
 */
const PanelMentionInput: FC<PanelMentionInputProps> = ({
  footer,
  isDisabled = false,
  onChangeText,
  onSelectPanel,
  placeholder,
  selectedPanels,
  value,
}) => {
  const inputRef = useRef<ComponentRef<typeof TextareaInput>>(null);
  const markerRef = useRef<ComponentRef<typeof RNText>>(null);
  const [caret, setCaret] = useState(0);
  const [dismissedQuery, setDismissedQuery] = useState<string | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [caretPos, setCaretPos] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const mention = useMemo(
    () => findMention(value, caret, selectedPanels),
    [caret, selectedPanels, value]
  );
  const showMenu = mention !== null && dismissedQuery !== mention.query;

  const segments = useMemo(
    () => toSegments(value, selectedPanels),
    [selectedPanels, value]
  );
  const selectedIds = useMemo(
    () => selectedPanels.map((panel) => panel.id),
    [selectedPanels]
  );

  /* RN-web only fires `onSelectionChange` on the DOM `select` event, so the
     caret is read straight from the node on change / focus. */
  const syncCaret = () => {
    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (node && typeof node.selectionStart === "number") {
      setCaret(node.selectionStart);
    }
  };

  const handleChange = (next: string) => {
    setDismissedQuery(null);
    onChangeText(next);
    syncCaret();
  };

  const handleScroll = (e: {
    nativeEvent?: { contentOffset?: { y: number } };
  }) => {
    const offset = e.nativeEvent?.contentOffset?.y;
    if (typeof offset === "number") {
      setScrollTop(offset);
      return;
    }
    const node = inputRef.current as unknown as HTMLTextAreaElement | null;
    if (node) setScrollTop(node.scrollTop);
  };

  /* Measure the caret's viewport position (web) so the body-portaled menu
     opens right under it — see `QuestionTextInput` for the rationale. */
  useLayoutEffect(() => {
    if (Platform.OS !== "web" || !showMenu) return;
    const marker = markerRef.current as unknown as HTMLElement | null;
    if (!marker?.getBoundingClientRect) return;
    const m = marker.getBoundingClientRect();
    setCaretPos({ left: m.left, top: m.bottom + 4 });
  }, [caret, scrollTop, showMenu, value]);

  const handleSelect = (panel: Panel) => {
    if (!mention) return;
    const before = value.slice(0, mention.start);
    const after = value.slice(mention.start + 1 + mention.query.length);
    const inserted = `@${panel.name} `;
    const nextCaret = before.length + inserted.length;
    onChangeText(`${before}${inserted}${after}`);
    onSelectPanel(panel);
    setCaret(nextCaret);
    setDismissedQuery(null);
    requestAnimationFrame(() => {
      const node = inputRef.current as unknown as HTMLTextAreaElement | null;
      node?.focus?.();
      node?.setSelectionRange?.(nextCaret, nextCaret);
    });
  };

  const menuBody = mention && (
    <MentionMenu
      excludeIds={selectedIds}
      onSelect={handleSelect}
      query={mention.query}
    />
  );

  /* Web: portal to <body> at fixed viewport coords so the menu sits above
     every sibling card regardless of stacking contexts. Nothing is rendered
     until the caret is measured — falling back to the inline branch for one
     frame would remount the menu (and refetch) as it moves into the portal. */
  const menu =
    Platform.OS === "web" ? (
      caretPos &&
      createPortal(
        <div
          style={{
            left: caretPos.left,
            position: "fixed",
            top: caretPos.top,
            zIndex: 9999,
          }}
        >
          {menuBody}
        </div>,
        document.body
      )
    ) : (
      <VStack
        className="absolute"
        style={{ left: 0, top: "100%", zIndex: 1000 }}
      >
        {menuBody}
      </VStack>
    );

  return (
    <VStack className="relative" style={showMenu ? { zIndex: 50 } : undefined}>
      {/* The footer lives inside the textarea itself, so the border, radius
          and focus ring belong to one element and it reads as one input. */}
      <Textarea
        className="h-auto rounded-xl border-outline-200 bg-background-50 data-[focus=true]:web:ring-0"
        isDisabled={isDisabled}
      >
        <VStack className="relative flex-1 min-h-[120px]">
          {/* Highlight mirror: plain glyphs + colored `@mentions` under the
            transparent input text, shifted by the input's scroll offset. */}
          <VStack
            className="absolute inset-0 px-2 py-2 overflow-hidden"
            style={{ pointerEvents: "none", zIndex: 0 }}
          >
            <RNText
              style={{
                fontSize: FONT_SIZE,
                lineHeight: LINE_HEIGHT,
                transform: [{ translateY: -scrollTop }],
                // @ts-expect-error - web-only wrapping style to mirror the textarea
                whiteSpace: "pre-wrap",
              }}
            >
              {segments.map((segment) => (
                <RNText
                  key={segment.key}
                  style={{
                    color: segment.mention ? MENTION_COLOR : PLAIN_COLOR,
                    fontSize: FONT_SIZE,
                    lineHeight: LINE_HEIGHT,
                  }}
                >
                  {segment.text}
                </RNText>
              ))}
            </RNText>
          </VStack>
          {/* Hidden mirror measuring the caret position for the menu (web). */}
          {Platform.OS === "web" && showMenu && (
            <VStack
              className="absolute inset-0 px-2 py-2"
              style={{ opacity: 0, pointerEvents: "none", zIndex: 0 }}
            >
              <RNText
                style={{
                  fontSize: FONT_SIZE,
                  lineHeight: LINE_HEIGHT,
                  transform: [{ translateY: -scrollTop }],
                  // @ts-expect-error - web-only wrapping style to mirror the textarea
                  whiteSpace: "pre-wrap",
                }}
              >
                {value.slice(0, caret)}
                <RNText ref={markerRef}>{"\u200b"}</RNText>
              </RNText>
            </VStack>
          )}
          <TextareaInput
            ref={inputRef}
            onChangeText={handleChange}
            onFocus={syncCaret}
            onKeyPress={({ nativeEvent }) => {
              if (nativeEvent.key === "Escape" && mention) {
                setDismissedQuery(mention.query);
              }
            }}
            onScroll={handleScroll}
            onSelectionChange={(e) => setCaret(e.nativeEvent.selection.start)}
            placeholder={placeholder}
            selectionColor={PLAIN_COLOR}
            style={{
              backgroundColor: "transparent",
              color: "transparent",
              fontSize: FONT_SIZE,
              lineHeight: LINE_HEIGHT,
              position: "relative",
              zIndex: 1,
              ...(Platform.OS === "web" ? { caretColor: PLAIN_COLOR } : null),
            }}
            value={value}
          />
        </VStack>
        {/* Fixed height so attaching an image never resizes the input. */}
        {footer && (
          <HStack className="h-16 w-full px-2 items-center border-t border-outline-200 bg-background-100 rounded-b-xl">
            {footer}
          </HStack>
        )}
      </Textarea>

      {showMenu && menu}
    </VStack>
  );
};

export default PanelMentionInput;
